/**
 * Os cards de mensagem recebida, no canto superior direito (#536).
 *
 * ── Foto, nome e a mensagem embaixo ──────────────────────────────────────
 *
 * Decidido em 25/09/2026: dentro da aba, o card mostra a foto de quem
 * escreveu, o nome e, embaixo, a mensagem. O conteúdo não passa por
 * terceiros — sai da nossa API, com o escopo reavaliado na leitura. Com a aba
 * escondida, o mesmo aviso vai também para o sistema operacional, só com nome
 * e mensagem (ver `aviso-do-sistema.ts`).
 *
 * ── Quando o card sai ────────────────────────────────────────────────────
 *
 * Três saídas, e as três marcam a notificação como vista — a partir daí ela
 * não volta em nenhuma aba:
 *
 *   1. a pessoa clica: abre a conversa;
 *   2. a pessoa dispensa no ×;
 *   3. a pessoa deixa passar: depois de `DURACAO_MS` o card desliza de volta
 *      para a direita — o caminho da entrada ao contrário — e aí sai.
 *
 * O relógio da terceira só corre com a aba À VISTA e sem o ponteiro ou o foco
 * sobre a pilha. Aba escondida é justamente quando ninguém viu o card; e
 * sumir enquanto a pessoa lê, ou está com o ponteiro indo ao botão, é o card
 * fugindo da mão dela.
 *
 * ── A conversa que já está na tela ───────────────────────────────────────
 *
 * Chegou mensagem da conversa que a pessoa está lendo: não há o que avisar —
 * a mensagem aparece na conversa. A notificação é marcada como vista sem card.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { AvisoDeAutomacao, Notificacao } from '@cliqcrm/contracts';
import { useMarcarNotificacoesVistas, useNotificacoes } from '@/shared/api/consultas';
import { useObservarRecurso } from '@/shared/api/assinaturas';
import { abaVisivelAgora, useAbaVisivel } from '@/shared/lib/aba-visivel';
import { Avatar, Icone } from '@/shared/ui';
import {
  avisarNoSistema,
  deveAvisarNoSistema,
  pedirPermissaoDoAviso,
  permissaoDoAviso,
  usePermissaoDoAviso,
} from '../model/aviso-do-sistema';
import { preferenciasDeNotificacao, usePreferenciasDeNotificacao } from '../model/preferencias';
import {
  mensagemDaNotificacao,
  nomeDaNotificacao,
  textoDoAviso,
} from '../lib/texto-da-notificacao';
import { novasParaAnunciar, visiveisNaPilha } from '../lib/regras-da-pilha';
import { anunciarVistas, ouvirVistas, reivindicar, travaDoAviso } from '../lib/entre-abas';
import './pilha-de-notificacoes.css';

/** Quanto o card fica à vista antes de sair sozinho (pedido de 28/09/2026). */
export const DURACAO_MS = 5_000;

/*
 * Rede de segurança da saída animada: o card sai de fato no fim da animação
 * (`animationend`), mas se o evento não vier — aba escondida no meio, movimento
 * reduzido em navegador que não dispara o evento com 0ms —, sai depois disto.
 * Um pouco mais que `--transicao-entrada` (260ms).
 */
const FOLGA_DA_SAIDA_MS = 400;

/*
 * Quanto a trava do aviso do sistema fica presa. As abas recebem a mesma
 * leitura com diferença de milissegundos a poucos segundos — a rebusca de
 * cada uma — e a que chegar depois disto ainda encontra a trava.
 */
const SEGURAR_AVISO_MS = 10_000;

/** Mais que isto empilhado já não é aviso, é fila — e a fila é a inbox. */
const MAXIMO_VISIVEL = 3;

// ── A pilha ───────────────────────────────────────────────────────────────

export interface PilhaDeNotificacoesProps {
  /**
   * O papel lê conversa? Sem isso não há card — e, mais importante, não há
   * assinatura: o servidor recusa o fluxo INTEIRO quando uma assinatura não é
   * autorizada, e o tempo real das outras telas cairia junto.
   */
  habilitada: boolean;
  /** A conversa aberta no atendimento, se houver. */
  conversaAberta: string | null;
  aoAbrirConversa: (conversaId: string) => void;
}

export function PilhaDeNotificacoes({
  habilitada,
  conversaAberta,
  aoAbrirConversa,
}: PilhaDeNotificacoesProps): ReactNode {
  /*
   * O que a pessoa escolheu no controle de notificações do atendimento. Com os
   * dois desligados não há o que avisar, e nem a assinatura se justifica.
   */
  const preferencias = usePreferenciasDeNotificacao();
  const ativa = habilitada && (preferencias.naTela || preferencias.foraDaTela);

  // Em qualquer tela: é o que mantém o fluxo aberto fora da inbox e do
  // atendimento, onde antes ele ficava `parado`.
  useObservarRecurso(ativa ? 'notificacao:eu' : null);

  const consulta = useNotificacoes(ativa);
  const marcar = useMarcarNotificacoesVistas();
  const abaVisivel = useAbaVisivel();
  const permissao = usePermissaoDoAviso();

  /*
   * O que saiu da tela nesta aba. A pilha esconde na hora, sem esperar o
   * servidor confirmar — senão o card dispensado piscaria de volta na rebusca
   * que a própria marcação provoca.
   */
  const [dispensadas, definirDispensadas] = useState<ReadonlySet<string>>(() => new Set());

  /*
   * O que já foi anunciado — card, e aviso do sistema quando coube. `null`
   * até a primeira leitura: o que já estava lá ao abrir o CRM aparece como
   * card, mas não vira balão do sistema, que avisaria do passado.
   */
  const anunciadas = useRef<Set<string> | null>(null);

  const itens = consulta.data?.itens ?? [];

  const dispensar = (ids: readonly string[]): void => {
    if (ids.length === 0) return;
    definirDispensadas((antes) => new Set([...antes, ...ids]));
    marcar.mutate(ids);
    // As outras abas tiram o mesmo card na hora — ver `entre-abas.ts`.
    anunciarVistas(ids);
  };

  /*
   * O que saiu em outra aba sai aqui também. Só esconde: quem marca no
   * servidor é a aba onde a pessoa agiu, e marcar de novo seria pedido à toa.
   */
  useEffect(
    () => ouvirVistas((ids) => definirDispensadas((antes) => new Set([...antes, ...ids]))),
    [],
  );

  const abrir = (notificacao: Notificacao): void => {
    dispensar([notificacao.id]);
    aoAbrirConversa(notificacao.conversa_id);
  };

  // A conversa que já está na tela não vira card — ver o cabeçalho.
  const daConversaAberta = abaVisivel
    ? itens.filter((n) => n.conversa_id === conversaAberta && !dispensadas.has(n.id))
    : [];
  const chaveDaAberta = daConversaAberta.map((n) => n.id).join(',');
  useEffect(() => {
    if (chaveDaAberta) dispensar(chaveDaAberta.split(','));
    // Só a chave: `dispensar` nasce novo a cada render, e como dependência
    // refaria o efeito sempre.
  }, [chaveDaAberta]);

  // O aviso do sistema, uma vez por notificação.
  useEffect(() => {
    if (consulta.data === undefined) return;
    const primeiraLeitura = anunciadas.current === null;
    const jaVistas = anunciadas.current ?? new Set<string>();
    anunciadas.current = jaVistas;

    for (const notificacao of novasParaAnunciar(consulta.data.itens, jaVistas)) {
      jaVistas.add(notificacao.id);
      if (primeiraLeitura) continue;
      if (
        deveAvisarNoSistema({
          abaVisivel: abaVisivelAgora(),
          permissao: permissaoDoAviso(),
          // Lido na hora, e não do render: a preferência pode ter mudado em
          // outra aba entre o render e a chegada da leitura.
          foraDaTela: preferenciasDeNotificacao().foraDaTela,
        })
      ) {
        // Várias abas escondidas recebem a mesma notificação: avisa só a que
        // pega a trava dela — ver `entre-abas.ts`.
        void reivindicar(travaDoAviso(notificacao.id), SEGURAR_AVISO_MS).then((minha) => {
          if (minha) avisarNoSistema(notificacao, () => abrir(notificacao));
        });
      }
    }
    // Só quando chega leitura nova. O `abrir` guardado no clique do balão é o
    // deste render, e basta: ele só dispensa e navega.
  }, [consulta.data]);

  const { visiveis, restantes } = visiveisNaPilha(
    itens,
    dispensadas,
    abaVisivel ? conversaAberta : null,
    MAXIMO_VISIVEL,
  );

  const [pausada, definirPausada] = useState(false);

  /*
   * Os avisos de automação (#903) ficam até a pessoa dispensar ou abrir: são
   * um pedido de atenção ao gestor, e sair sozinho em cinco segundos seria
   * perdê-lo. Vêm antes das mensagens.
   */
  const avisos = (consulta.data?.avisos ?? []).filter((a) => !dispensadas.has(a.id));
  const abrirAviso = (aviso: AvisoDeAutomacao): void => {
    dispensar([aviso.id]);
    if (aviso.conversa_id) aoAbrirConversa(aviso.conversa_id);
  };

  // Cards desligados: a pilha não aparece, e a notificação fica pendente — ela
  // continua na fila da inbox, e o aviso fora da tela (se ligado) segue valendo.
  if (!ativa || !preferencias.naTela || (visiveis.length === 0 && avisos.length === 0)) {
    return null;
  }

  return (
    <section
      className="pilha-de-notificacoes"
      aria-label="Mensagens recebidas"
      onMouseEnter={() => definirPausada(true)}
      onMouseLeave={() => definirPausada(false)}
      onFocus={() => definirPausada(true)}
      onBlur={(evento) => {
        if (!evento.currentTarget.contains(evento.relatedTarget as Node | null)) {
          definirPausada(false);
        }
      }}
    >
      {/*
        `polite`: o leitor de tela anuncia o card novo quando terminar a frase
        em curso. `assertive` interromperia a pessoa no meio de uma resposta
        ao cliente — que é exatamente o que ela está fazendo.
      */}
      <ul className="pilha-de-notificacoes__lista" aria-live="polite" aria-relevant="additions">
        {avisos.map((aviso) => (
          <CardDeAviso
            key={aviso.id}
            aviso={aviso}
            aoAbrir={() => abrirAviso(aviso)}
            aoDispensar={() => dispensar([aviso.id])}
          />
        ))}
        {visiveis.map((notificacao) => (
          <CardDeNotificacao
            key={notificacao.id}
            notificacao={notificacao}
            correndo={abaVisivel && !pausada}
            aoAbrir={() => abrir(notificacao)}
            aoDispensar={() => dispensar([notificacao.id])}
          />
        ))}
      </ul>

      {(restantes > 0 || (permissao === 'default' && preferencias.foraDaTela)) && (
        <div className="pilha-de-notificacoes__rodape">
          {restantes > 0 && (
            <span className="pilha-de-notificacoes__mais">
              {restantes === 1 ? 'mais 1 mensagem' : `mais ${restantes} mensagens`}
            </span>
          )}
          {/*
            O pedido de permissão mora AQUI, no momento em que ele faz
            sentido: a pessoa acabou de ver o card e entende o que está
            autorizando. Pedido do navegador sem gesto é bloqueado — ver
            `aviso-do-sistema.ts`.
          */}
          {permissao === 'default' && preferencias.foraDaTela && (
            <button
              type="button"
              className="pilha-de-notificacoes__permitir"
              onClick={() => void pedirPermissaoDoAviso()}
            >
              Avisar também quando o CRM estiver em outra aba
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ── Um card ───────────────────────────────────────────────────────────────

function CardDeNotificacao({
  notificacao,
  correndo,
  aoAbrir,
  aoDispensar,
}: {
  notificacao: Notificacao;
  /** O relógio de saída corre? Ver o cabeçalho, saída 3. */
  correndo: boolean;
  aoAbrir: () => void;
  aoDispensar: () => void;
}): ReactNode {
  const nome = nomeDaNotificacao(notificacao);
  const mensagem = mensagemDaNotificacao(notificacao);

  /*
   * Recomeça do zero a cada retomada, e não do que faltava. Guardar o resto
   * exigiria medir o tempo pausado; o custo de recomeçar é o card ficar alguns
   * segundos a mais para quem acabou de passar o ponteiro por cima — que é
   * quem está olhando para ele.
   */
  const aoDispensarAtual = useRef(aoDispensar);
  aoDispensarAtual.current = aoDispensar;

  /*
   * Deixado passar, o card primeiro SAI da tela — desliza para a direita, o
   * caminho da entrada ao contrário — e só no fim da animação é dispensado. O
   * × e o clique continuam tirando na hora: são gesto da pessoa, e esperar a
   * animação para responder a um clique pareceria lentidão.
   */
  const [saindo, definirSaindo] = useState(false);
  const dispensado = useRef(false);
  const concluirSaida = (): void => {
    if (dispensado.current) return;
    dispensado.current = true;
    aoDispensarAtual.current();
  };

  useEffect(() => {
    if (!correndo || saindo) return;
    const relogio = setTimeout(() => definirSaindo(true), DURACAO_MS);
    return () => clearTimeout(relogio);
  }, [correndo, saindo]);

  useEffect(() => {
    if (!saindo) return;
    const folga = setTimeout(concluirSaida, FOLGA_DA_SAIDA_MS);
    return () => clearTimeout(folga);
  }, [saindo]);

  return (
    <li
      className="pilha-de-notificacoes__item"
      data-saindo={saindo ? 'sim' : undefined}
      onAnimationEnd={(evento) => {
        // Só a animação do próprio item: a de um filho também borbulha até aqui.
        if (saindo && evento.target === evento.currentTarget) concluirSaida();
      }}
    >
      <article className="notificacao">
        <button type="button" className="notificacao__abrir" onClick={aoAbrir}>
          <Avatar nome={nome} foto={notificacao.cliente.avatar_url} tamanho={34} />
          <span className="notificacao__corpo">
            <span className="notificacao__nome">{nome}</span>
            <span className="notificacao__mensagem">{mensagem}</span>
          </span>
        </button>
        <button
          type="button"
          className="notificacao__dispensar"
          aria-label={`Dispensar a mensagem de ${nome}`}
          title="Dispensar"
          onClick={aoDispensar}
        >
          <span aria-hidden="true">×</span>
        </button>
      </article>
    </li>
  );
}

// ── Um aviso de automação ─────────────────────────────────────────────────

function CardDeAviso({
  aviso,
  aoAbrir,
  aoDispensar,
}: {
  aviso: AvisoDeAutomacao;
  aoAbrir: () => void;
  aoDispensar: () => void;
}): ReactNode {
  const texto = textoDoAviso(aviso);
  return (
    <li className="pilha-de-notificacoes__item">
      <article className="notificacao">
        <button type="button" className="notificacao__abrir" onClick={aoAbrir}>
          <Icone nome="automacao" />
          <span className="notificacao__corpo">
            <span className="notificacao__nome">Automação</span>
            <span className="notificacao__mensagem">{texto}</span>
          </span>
        </button>
        <button
          type="button"
          className="notificacao__dispensar"
          aria-label={`Dispensar o aviso: ${texto}`}
          title="Dispensar"
          onClick={aoDispensar}
        >
          <span aria-hidden="true">×</span>
        </button>
      </article>
    </li>
  );
}
